import React from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, PlugZap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, SectionCard, Modal, EmptyState, CardSkeleton, StatusBadge, ConfirmModal } from "@/admin/ui";

const EMPTY = {
  name: "", product_id: "", provider_key: "", base_url: "", test_url: "",
  api_key: "", secret: "", username: "", password: "",
  headers: "{}", timeout_seconds: 20, retry_count: 2, cache_seconds: 300,
  daily_quota: "", monthly_quota: "", provider_cost_usd: 0,
  commercial_use: false, resale_allowed: false, attribution_text: "", license_note: "",
  active: false,
};

function Field({ label, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{label}</label>
      <input type={type} required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="lp-input" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

export default function AdminResources() {
  const { t } = useLang();
  const [items, setItems] = React.useState(null);
  const [products, setProducts] = React.useState([]);
  const [modal, setModal] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(null);

  const load = async () => {
    const [r, p] = await Promise.all([
      api.get("/api/admin/resources"),
      api.get("/api/admin/products"),
    ]);
    setItems(r.data.items);
    setProducts(p.data.items);
  };
  React.useEffect(() => { load(); }, []);

  const doTest = async (id) => {
    try {
      const { data } = await api.post(`/api/admin/resources/${id}/test`);
      if (data.ok) toast.success(`OK (${data.status})`);
      else toast.error(data.error || `Failed (${data.status})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const doDelete = async () => {
    await api.delete(`/api/admin/resources/${deleteId}`);
    toast.success(t("common.deleted")); load();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = { ...modal.data };
      try { d.headers = JSON.parse(d.headers || "{}"); } catch { toast.error("Headers is invalid JSON"); setBusy(false); return; }
      if (d.daily_quota === "") d.daily_quota = null;
      if (d.monthly_quota === "") d.monthly_quota = null;
      d.timeout_seconds = Number(d.timeout_seconds);
      d.retry_count = Number(d.retry_count);
      d.cache_seconds = Number(d.cache_seconds);
      d.provider_cost_usd = Number(d.provider_cost_usd);
      ["api_key", "secret", "username", "password"].forEach((k) => { if (!d[k] || !String(d[k]).trim()) delete d[k]; });
      if (modal.mode === "create") await api.post("/api/admin/resources", d);
      else await api.put(`/api/admin/resources/${modal.data.id}`, d);
      toast.success(t("common.saved"));
      setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-resources-page">
      <PageHeader title={t("admin.resources.title")} description={t("admin.resources.subtitle")}
        primary={<button className="lp-btn-primary text-xs py-1.5 px-3" onClick={() => setModal({ mode: "create", data: { ...EMPTY } })} data-testid="add-resource">
          <Plus className="w-3.5 h-3.5" /> {t("admin.resources.add")}
        </button>} />

      {!items ? <CardSkeleton h="h-40" /> : items.length === 0 ? (
        <EmptyState title={t("admin.resources.empty")} />
      ) : (
        <div className="grid gap-3">
          {items.map((r) => {
            const product = products.find((p) => p.id === r.product_id);
            return (
              <SectionCard key={r.id} testid={`resource-${r.id}`}
                title={<span className="flex items-center gap-2 flex-wrap">
                  {r.name}
                  <StatusBadge tone={r.active ? "success" : "danger"}>{r.active ? "active" : "inactive"}</StatusBadge>
                  {r.commercial_use && <StatusBadge tone="brand">commercial</StatusBadge>}
                  {r.resale_allowed && <StatusBadge tone="brand">resale</StatusBadge>}
                  {r.last_health_ok ? <StatusBadge tone="success">healthy</StatusBadge>
                    : r.last_health_error ? <StatusBadge tone="danger">error</StatusBadge>
                    : <StatusBadge tone="neutral">untested</StatusBadge>}
                </span>}
                description={<span>{product?.name || r.product_id} · <span className="font-mono">{r.base_url}</span></span>}>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2 text-xs text-white/60 font-mono">
                  <div>key: {r.api_key_mask || "—"}</div>
                  <div>secret: {r.secret_mask || "—"}</div>
                  <div>{t("admin.resources.timeout")}: {r.timeout_seconds}s</div>
                  <div>{t("admin.resources.cache")}: {r.cache_seconds}s</div>
                  <div>{t("admin.resources.last_ok")}: {r.last_health_ok || "—"}</div>
                  <div className="text-red-300 truncate">{t("admin.resources.last_err")}: {r.last_health_error || "—"}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button className="lp-btn-secondary text-xs py-1.5 px-3" onClick={() => doTest(r.id)} data-testid={`resource-test-${r.id}`}>
                    <PlugZap className="w-3.5 h-3.5" /> {t("admin.resources.test")}
                  </button>
                  <button className="lp-btn-secondary text-xs py-1.5 px-3" onClick={() => setModal({ mode: "edit", data: { ...r, api_key: "", secret: "", username: "", password: "", headers: JSON.stringify(r.headers || {}, null, 2) } })} data-testid={`resource-edit-${r.id}`}>
                    <Save className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button className="lp-btn-danger text-xs" onClick={() => setDeleteId(r.id)} data-testid={`resource-delete-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> {t("admin.resources.delete")}
                  </button>
                </div>
              </SectionCard>
            );
          })}
        </div>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} size="xl"
        title={modal?.mode === "create" ? t("admin.resources.add") : "Edit resource"}
        testid="resource-modal"
        footer={<>
          <button type="button" className="lp-btn-secondary" onClick={() => setModal(null)}>{t("common.cancel")}</button>
          <button type="submit" form="resource-form" disabled={busy} className="lp-btn-primary" data-testid="resource-submit">{t("common.save")}</button>
        </>}>
        {modal && (
          <form id="resource-form" onSubmit={submit} className="grid md:grid-cols-2 gap-3">
            <Field label={t("admin.resources.name")} value={modal.data.name} onChange={(v) => setModal({ ...modal, data: { ...modal.data, name: v } })} required testid="res-name" />
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.resources.product")}</label>
              <select className="lp-input" required value={modal.data.product_id} onChange={(e) => setModal({ ...modal, data: { ...modal.data, product_id: e.target.value } })} data-testid="res-product">
                <option value="">—</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.resources.provider_key")}</label>
              <select className="lp-input" value={modal.data.provider_key || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, provider_key: e.target.value } })} data-testid="res-provider-key">
                <option value="">—</option>
                <option value="ipinfo">ipinfo</option>
                <option value="frankfurter">frankfurter</option>
                <option value="nager">nager</option>
                <option value="custom">custom</option>
              </select>
            </div>
            <Field label={t("admin.resources.base_url")} value={modal.data.base_url} onChange={(v) => setModal({ ...modal, data: { ...modal.data, base_url: v } })} required testid="res-base" />
            <Field label={t("admin.resources.test_url")} value={modal.data.test_url} onChange={(v) => setModal({ ...modal, data: { ...modal.data, test_url: v } })} testid="res-test-url" />
            <Field label={t("admin.resources.api_key")} value={modal.data.api_key} onChange={(v) => setModal({ ...modal, data: { ...modal.data, api_key: v } })} placeholder={modal.data.api_key_mask || ""} testid="res-api-key" />
            <Field label={t("admin.resources.secret")} value={modal.data.secret} onChange={(v) => setModal({ ...modal, data: { ...modal.data, secret: v } })} placeholder={modal.data.secret_mask || ""} testid="res-secret" />
            <Field label={t("admin.resources.username")} value={modal.data.username} onChange={(v) => setModal({ ...modal, data: { ...modal.data, username: v } })} testid="res-username" />
            <Field label={t("admin.resources.password")} value={modal.data.password} onChange={(v) => setModal({ ...modal, data: { ...modal.data, password: v } })} testid="res-password" type="password" />
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.resources.headers")}</label>
              <textarea className="lp-input font-mono text-xs min-h-[80px]" value={modal.data.headers} onChange={(e) => setModal({ ...modal, data: { ...modal.data, headers: e.target.value } })} />
            </div>
            <Field label={t("admin.resources.timeout")} type="number" value={modal.data.timeout_seconds} onChange={(v) => setModal({ ...modal, data: { ...modal.data, timeout_seconds: v } })} />
            <Field label={t("admin.resources.retries")} type="number" value={modal.data.retry_count} onChange={(v) => setModal({ ...modal, data: { ...modal.data, retry_count: v } })} />
            <Field label={t("admin.resources.cache")} type="number" value={modal.data.cache_seconds} onChange={(v) => setModal({ ...modal, data: { ...modal.data, cache_seconds: v } })} />
            <Field label={t("admin.resources.daily")} type="number" value={modal.data.daily_quota} onChange={(v) => setModal({ ...modal, data: { ...modal.data, daily_quota: v } })} />
            <Field label={t("admin.resources.monthly")} type="number" value={modal.data.monthly_quota} onChange={(v) => setModal({ ...modal, data: { ...modal.data, monthly_quota: v } })} />
            <Field label={t("admin.resources.cost")} type="number" value={modal.data.provider_cost_usd} onChange={(v) => setModal({ ...modal, data: { ...modal.data, provider_cost_usd: v } })} />
            <Field label={t("admin.resources.attribution")} value={modal.data.attribution_text} onChange={(v) => setModal({ ...modal, data: { ...modal.data, attribution_text: v } })} />
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.resources.license")}</label>
              <textarea className="lp-input text-sm min-h-[60px]" value={modal.data.license_note} onChange={(e) => setModal({ ...modal, data: { ...modal.data, license_note: e.target.value } })} />
            </div>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={!!modal.data.commercial_use} onChange={(e) => setModal({ ...modal, data: { ...modal.data, commercial_use: e.target.checked } })} data-testid="res-commercial" />
              <span className="text-white/70 text-sm">{t("admin.resources.commercial")}</span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={!!modal.data.resale_allowed} onChange={(e) => setModal({ ...modal, data: { ...modal.data, resale_allowed: e.target.checked } })} data-testid="res-resale" />
              <span className="text-white/70 text-sm">{t("admin.resources.resale")}</span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={!!modal.data.active} onChange={(e) => setModal({ ...modal, data: { ...modal.data, active: e.target.checked } })} data-testid="res-active" />
              <span className="text-white/70 text-sm">{t("admin.resources.active")}</span>
            </label>
          </form>
        )}
      </Modal>

      <ConfirmModal open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={doDelete}
        title="Delete resource" description="This will remove the resource. Existing calls in-flight are unaffected." actionLabel="Delete" danger />
    </div>
  );
}
